/**
 * ReferralCodeInput - Input component for entering and validating referral codes.
 * Provides visual feedback for validation states with JumpBean's playful aesthetic.
 */

import { useState, useCallback, useEffect } from "react";
import { trackEvent } from "@jumpbean/analytics";

export interface ReferralCodeInputProps {
  value: string;
  onChange: (value: string) => void;
  isValid: boolean | null;
  validationMessage?: string;
  referrerName?: string;
  onValidate: (code: string) => Promise<void>;
  disabled?: boolean;
  autoFocus?: boolean;
}

export function ReferralCodeInput({
  value,
  onChange,
  isValid,
  validationMessage,
  referrerName,
  onValidate,
  disabled = false,
  autoFocus = false,
}: ReferralCodeInputProps) {
  const [isValidating, setIsValidating] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  // Trigger success animation when code becomes valid
  useEffect(() => {
    if (isValid === true) {
      setShowSuccess(true);
      const timer = setTimeout(() => setShowSuccess(false), 600);
      return () => clearTimeout(timer);
    }
  }, [isValid]);

  // Track validation result
  useEffect(() => {
    if (isValid !== null && value.trim()) {
      trackEvent("referral_code_validated", {
        code: value.trim(),
        valid: isValid,
        referrer_name: referrerName,
      });
    }
  }, [isValid, value, referrerName]);

  const handleValidate = useCallback(async () => {
    if (!value.trim() || isValidating || disabled) return;

    setIsValidating(true);
    try {
      await onValidate(value.trim().toUpperCase());
    } finally {
      setIsValidating(false);
    }
  }, [value, isValidating, disabled, onValidate]);

  const handleBlur = useCallback(() => {
    setIsFocused(false);
    // Auto-validate on blur if there's a value and it hasn't been validated yet
    if (value.trim() && isValid === null) {
      handleValidate();
    }
  }, [value, isValid, handleValidate]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Enter") {
        e.preventDefault();
        handleValidate();
      }
    },
    [handleValidate]
  );

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      // Uppercase and remove spaces
      const cleaned = e.target.value.toUpperCase().replace(/\s/g, "");
      onChange(cleaned);
    },
    [onChange]
  );

  // Determine border and ring colors based on state
  const getBorderClasses = () => {
    if (isValid === true) {
      return "border-meadow-400 focus:border-meadow-500 focus:ring-meadow-100";
    }
    if (isValid === false) {
      return "border-fairy-400 focus:border-fairy-500 focus:ring-fairy-100";
    }
    return "border-neutral-200 focus:border-magic-400 focus:ring-magic-100";
  };

  return (
    <div className="relative">
      <label
        htmlFor="referral-code"
        className="block font-body font-semibold text-neutral-700 mb-2 text-body-md"
      >
        Have a referral code?
      </label>

      <div className="relative">
        <input
          type="text"
          id="referral-code"
          value={value}
          onChange={handleChange}
          onFocus={() => setIsFocused(true)}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          placeholder="Enter code (e.g., SARAH-X7K2)"
          disabled={disabled || isValidating}
          autoFocus={autoFocus}
          autoComplete="off"
          spellCheck={false}
          className={`
            w-full px-5 py-4 pr-14
            bg-white
            border-[3px] rounded-2xl
            font-mono font-semibold text-body-md text-neutral-800 uppercase tracking-wider
            placeholder:text-neutral-400 placeholder:font-body placeholder:font-normal placeholder:normal-case placeholder:tracking-normal
            transition-all duration-200
            focus:outline-none focus:ring-4
            disabled:opacity-50 disabled:cursor-not-allowed
            ${getBorderClasses()}
            ${showSuccess ? "animate-[successPulse_0.4s_ease-out]" : ""}
          `}
          aria-invalid={isValid === false ? "true" : "false"}
          aria-describedby={validationMessage ? "referral-code-message" : undefined}
        />

        {/* Status indicator */}
        <div className="absolute right-4 top-1/2 -translate-y-1/2">
          {isValidating ? (
            <div className="w-8 h-8 flex items-center justify-center">
              <LoadingSpinner />
            </div>
          ) : isValid === true ? (
            <div className="w-8 h-8 bg-meadow-500 rounded-full flex items-center justify-center animate-[bounceIn_0.4s_ease-out]">
              <CheckIcon className="w-5 h-5 text-white" />
            </div>
          ) : isValid === false ? (
            <div className="w-8 h-8 bg-fairy-500 rounded-full flex items-center justify-center animate-[shake_0.4s_ease-out]">
              <XIcon className="w-5 h-5 text-white" />
            </div>
          ) : value.trim() && !isFocused ? (
            <button
              type="button"
              onClick={handleValidate}
              className="
                px-3 py-1.5
                bg-magic-100 hover:bg-magic-200
                text-magic-700 font-display font-semibold text-body-sm
                rounded-full
                transition-colors duration-200
                focus:outline-none focus-visible:ring-2 focus-visible:ring-magic-400
              "
            >
              Check
            </button>
          ) : null}
        </div>
      </div>

      {/* Validation message or success banner */}
      {isValid === true && referrerName && (
        <div
          id="referral-code-message"
          className="
            mt-3 p-3
            bg-meadow-50 border-2 border-meadow-200
            rounded-xl
            flex items-center gap-2
            animate-[slideDown_0.3s_ease-out]
          "
        >
          <span className="text-lg">✓</span>
          <p className="font-body text-body-sm text-meadow-700">
            Referred by <span className="font-semibold">{referrerName}</span>
          </p>
        </div>
      )}

      {isValid === false && validationMessage && (
        <p
          id="referral-code-message"
          className="mt-2 font-body text-body-sm text-fairy-600 flex items-center gap-2 animate-[slideDown_0.2s_ease-out]"
          role="alert"
        >
          <AlertIcon />
          {validationMessage}
        </p>
      )}
    </div>
  );
}

// Icons
function CheckIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={3}
    >
      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
    </svg>
  );
}

function XIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={3}
    >
      <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
    </svg>
  );
}

function AlertIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="flex-shrink-0"
    >
      <circle cx="12" cy="12" r="10" />
      <line x1="12" y1="8" x2="12" y2="12" />
      <line x1="12" y1="16" x2="12.01" y2="16" />
    </svg>
  );
}

function LoadingSpinner() {
  return (
    <svg
      className="w-5 h-5 animate-spin text-magic-500"
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
    >
      <circle
        className="opacity-25"
        cx="12"
        cy="12"
        r="10"
        stroke="currentColor"
        strokeWidth="4"
      />
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
      />
    </svg>
  );
}

export default ReferralCodeInput;
